import React, { useState, useEffect, useCallback } from "react";
import { announcementsAPI } from "../lib/api";
import { X, Info, AlertTriangle, AlertCircle, Sparkles } from "lucide-react";
import { cn } from "../lib/utils";

const SUPPORT_EMAIL = process.env.REACT_APP_SUPPORT_EMAIL || "support@freelancetrackit.app";

const typeConfig = {
  info: {
    icon: Info,
    className: "bg-blue-50 border-blue-200 text-blue-800 dark:bg-blue-950 dark:border-blue-800 dark:text-blue-200",
    iconClass: "text-blue-500 dark:text-blue-400",
  },
  warning: {
    icon: AlertTriangle,
    className: "bg-yellow-50 border-yellow-200 text-yellow-800 dark:bg-yellow-950 dark:border-yellow-800 dark:text-yellow-200",
    iconClass: "text-yellow-500 dark:text-yellow-400",
  },
  critical: {
    icon: AlertCircle,
    className: "bg-red-50 border-red-200 text-red-800 dark:bg-red-950 dark:border-red-800 dark:text-red-200",
    iconClass: "text-red-500 dark:text-red-400",
  },
  motivational: {
    icon: Sparkles,
    className: "bg-green-50 border-green-200 text-green-800 dark:bg-green-950 dark:border-green-800 dark:text-green-200",
    iconClass: "text-green-500 dark:text-green-400",
  },
};

export default function AnnouncementBanner() {
  const [announcements, setAnnouncements] = useState([]);
  const [dismissed, setDismissed] = useState(new Set());

  const fetchAnnouncements = useCallback(async () => {
    try {
      const res = await announcementsAPI.getActive();
      setAnnouncements(res.data || []);
    } catch {
      // silently ignore — non-critical
    }
  }, []);

  useEffect(() => {
    fetchAnnouncements();
    const interval = setInterval(fetchAnnouncements, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchAnnouncements]);

  const handleDismiss = async (id) => {
    setDismissed((prev) => new Set([...prev, id]));
    try {
      await announcementsAPI.dismiss(id);
    } catch {
      // silently ignore
    }
  };

  const visible = announcements.filter(
    (a) => !dismissed.has(a.id)
  );

  if (visible.length === 0) return null;

  return (
    <div className="space-y-1 no-print" data-testid="announcement-banner-container">
      {visible.map((ann) => {
        const config = typeConfig[ann.type] || typeConfig.info;
        const Icon = config.icon;
        return (
          <div
            key={ann.id}
            className={cn(
              "flex items-start gap-3 px-4 py-3 border-b text-sm",
              config.className
            )}
            role="alert"
            data-testid={`announcement-${ann.id}`}
          >
            <Icon className={cn("h-4 w-4 mt-0.5 shrink-0", config.iconClass)} />
            <div className="flex-1 min-w-0">
              {ann.title && (
                <span className="font-semibold mr-2">{ann.title}:</span>
              )}
              <span>{ann.message}</span>
              <span className="ml-2 text-xs opacity-70">
                Questions? Email{" "}
                <a
                  href={`mailto:${SUPPORT_EMAIL}`}
                  className="underline hover:no-underline"
                >
                  {SUPPORT_EMAIL}
                </a>
              </span>
            </div>
            {ann.dismissible && (
              <button
                onClick={() => handleDismiss(ann.id)}
                className="shrink-0 p-0.5 rounded hover:opacity-70 transition-opacity"
                aria-label="Dismiss announcement"
                data-testid={`dismiss-announcement-${ann.id}`}
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
